/* eslint-disable @typescript-eslint/indent */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable multiline-ternary */
import classnames from 'classnames'
import { Component } from 'preact'
import { isInputEl } from '../../libs/typeGuards'
import Icon from '../assets'
import ButtonControl from '../button-control'
import Dropdown from './index'

export interface Item<T> {
  value: T
  label: string
}

export interface ItemGroup<T> {
  items: Array<Item<T>>
  label: string
}

interface Props<T> {
  optgroup: Array<ItemGroup<T>>
  class?: string
  placeholder?: string
  selectedValue?: Item<T>['value']
  onUpdate: (value: Item<T>['value']) => void
  newStyle?: boolean
}

export class DropdownSelectizeGroup<T extends any> extends Component<Props<T>> {
  onSelect(item: Item<T>) {
    this.props.onUpdate(item.value)
  }

  getSelectedItem(): Item<T> | null {
    let selected: Item<T> | null = null
    this.props.optgroup.forEach((group) => {
      group.items.forEach((item) => {
        if (item.value !== this.props.selectedValue) return
        selected = item
      })
    })
    return selected
  }

  render() {
    const selectedLabel = this.getSelectedItem()?.label ?? this.props.placeholder
    const DropdownTrigger = (props: { onClick: (e: Event) => void }) => {
      if (this.props.newStyle) {
        return (
          <ButtonControl
            newStyle
            title={
              <div>
                {selectedLabel}
                <Icon icon='chevronDown' height={16} width={16} />
              </div>
            }
            size='medium'
            class='button-control--dropdown'
            onClick={props.onClick}
          />
        )
      }
      return (
        <div className='selectize-input' onClick={props.onClick}>
          <div className='item'>{selectedLabel}</div>
        </div>
      )
    }
    return (
      <div className={classnames('selectize-control', 'single', this.props.class)}>
        <Dropdown
          popupName='dropdown-selectize'
          closeOnDropdownClick
          triggerElement={DropdownTrigger as any}
          class={classnames(
            'selectize-dropdown',
            'single',
            this.props.class,
            'preact-selectize-dropdown'
          )}
        >
          <div className='selectize-dropdown-content'>
            {this.props.optgroup.map((group, index) => {
              return (
                <div key={index} className='optgroup'>
                  <div className='optgroup-header'>{group.label}</div>
                  {group.items.map((item, index) => {
                    return (
                      <div
                        key={index}
                        className='option'
                        data-selectable
                        data-value={item.value}
                        onClick={() => this.onSelect(item)}
                      >
                        {item.label}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </Dropdown>
      </div>
    )
  }
}

type SingleOrMultiSelect =
  | {
      single: true | undefined
      onChange: (i: string) => void
      selectedItem: string
    }
  | {
      single: false
      onChange: (i: string[]) => void
      selectedItems: string[]
    }

type InputProps = SingleOrMultiSelect & {
  items: string[]
  placeholder: string
  class?: string
  allowEmptyValue?: false | boolean
}

interface InputState {
  searchText: string
  inputInFocus: boolean
}

export class DropdownSelectizeInput extends Component<InputProps, InputState> {
  inputRef: HTMLElement | null = null

  constructor() {
    super()
    this.state = {
      searchText: '',
      inputInFocus: false
    }
  }

  onSearchUpdated(newSearchText: string) {
    this.setState({
      searchText: newSearchText,
      inputInFocus: true
    })
  }

  selectItem(item: string) {
    if (!item && !this.props.allowEmptyValue) return
    if (!item && this.props.allowEmptyValue && item !== '') return
    if (this.props.single !== false) {
      this.props.onChange(item)
      return
    }
    if (!this.props.selectedItems.includes(item)) {
      this.props.onChange(this.props.selectedItems.concat(item))
    }
    this.setState({ searchText: '' })
  }

  getItems() {
    const predicate = (item: string) => {
      if (this.props.single !== false) {
        return true
      }
      return !this.props.selectedItems.find((i) => i === item)
    }

    return this.props.items
      .filter(predicate)
      .filter((item) => item.toLowerCase().includes(this.state.searchText))
  }

  componentWillReceiveProps(nextProps: InputProps) {
    if (nextProps.single === false) return
    if (this.state.searchText !== nextProps.selectedItem) {
      this.setState({ searchText: nextProps.selectedItem })
    }
  }

  showNewBox() {
    if (this.props.single !== false) {
      return false
    }
    return this.props.items.length && this.state.searchText
  }

  remove(item: string) {
    if (this.props.single !== false) return
    this.props.onChange(this.props.selectedItems.filter((i) => i !== item))
  }

  render() {
    const items = this.getItems()
    const showNewBox = this.showNewBox()
    const selectizeClass = this.props.single !== false ? 'single' : 'multi'
    const hasItems = this.props.single === false && this.props.selectedItems.length
    return (
      <div
        className={classnames(
          'selectize-control',
          this.props.class,
          selectizeClass,
          'plugin-remove_button',
          'preact-selectize-dropdown'
        )}
      >
        <div
          className={classnames('selectize-input', 'items', 'has-options', 'not-full', {
            'selectize-input-active': this.state.inputInFocus,
            'has-items': hasItems
          })}
          onClick={() => {
            if (this.inputRef) {
              this.inputRef.focus()
            }
          }}
        >
          {this.props.single === false &&
            this.props.selectedItems.map((item, index) => {
              return (
                <div key={index} className='item' data-value={item}>
                  {item}
                  <a
                    href='#'
                    className='remove'
                    onClick={(e) => {
                      e.preventDefault()
                      this.remove(item)
                    }}
                    title='Remove'
                  >
                    <Icon icon='close' width={12} height={12} color='currentColor' />
                  </a>
                </div>
              )
            })}
          <input
            placeholder={hasItems ? '' : this.props.placeholder}
            type='text'
            onFocus={() => this.setState({ inputInFocus: true })}
            onBlur={() =>
              setTimeout(() => {
                this.setState({ inputInFocus: false })
              }, 100)
            }
            onInput={(e) => {
              if (!isInputEl(e.target)) return
              this.onSearchUpdated(e.target.value)
            }}
            onChange={() => {
              this.selectItem(this.state.searchText)
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 9) {
                this.selectItem(this.state.searchText)
                e.preventDefault()
              }
            }}
            value={this.state.searchText}
            ref={(ref) => {
              this.inputRef = ref
            }}
          />
        </div>

        {this.state.inputInFocus && (items.length || showNewBox) ? (
          <div
            className={classnames(
              'selectize-dropdown',
              selectizeClass,
              this.props.class,
              'preact-selectize-dropdown'
            )}
          >
            <div className='selectize-dropdown-content'>
              {items.map((item, index) => (
                <div
                  key={index}
                  className='option'
                  data-selectable
                  data-value={item}
                  onMouseDown={() => this.selectItem(item)}
                >
                  {item}
                </div>
              ))}
              {showNewBox ? (
                <div
                  data-selectable
                  className='create'
                  onMouseDown={() => this.selectItem(this.state.searchText)}
                >
                  Add <strong>{this.state.searchText} ...</strong>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    )
  }
}
